// Shared attention list for Firefly (float) and the notification bell (docked).
// Audiences and landings follow docs/design/gating_audit.md non-ghost rows
// and docs/planning/attention_catalog.md.

import type { KpiTone } from "@/components/common/kpi/kpiTypes";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import {
  holdsLiteralAudienceRole,
  type DeriveActivityDockStatusInput,
} from "@/components/activity/activityDockStatus";
import type { ActivityDockStatusReasonCode } from "@/components/activity/fireflyStatusReason";
import type { AuthContextValue } from "@/contexts/AuthContext";

const SKIP_SECTION_IDS = new Set([
  "messages",
  "achievements",
  "logistics-dispatch-reminder",
  "production-floor-reminder",
  "cogs-period-reminder",
]);

const UPDATE_SECTION_IDS = new Set([
  "active-lead-checklist",
  "daily-checklist",
  "platform-context",
]);

export type AttentionSectionGroups = {
  needsYou: string[];
  updates: string[];
  history: string[];
};

export type AttentionListInput = {
  auth: AuthContextValue | null | undefined;
  sectionIds: readonly string[];
  canOpenMode: (mode: PrimaryMode) => boolean;
  hasOpenCallback: Partial<Record<ActivityDockStatusReasonCode, boolean>>;
  unsentCount: number;
  openQcCount: number;
  negativeOnHandCount: number;
  plantRequestReviewCount: number;
  plantStatusCount: number;
  staleWacCount: number;
  kbGapOpenCount: number;
  aiPromptingOpenCount: number;
  pendingMemberApprovalTotal: number;
  orgUnsentRollupCount: number;
  adsTone: KpiTone | null;
};

export type AttentionList = {
  groups: AttentionSectionGroups;
  showNegativeOnHand: boolean;
  landingReady: NonNullable<DeriveActivityDockStatusInput["landingReady"]>;
};

function literal(auth: AttentionListInput["auth"], roles: readonly string[]): boolean {
  return holdsLiteralAudienceRole(auth, roles);
}

function callbackReady(
  input: AttentionListInput,
  code: ActivityDockStatusReasonCode,
): boolean {
  return input.hasOpenCallback[code] !== false;
}

/**
 * Which dock sections belong in each category, plus the landing gate that
 * `deriveActivityDockStatus` uses so a glow cannot outlive its link.
 */
export function listAttentionItems(input: AttentionListInput): AttentionList {
  const can = input.canOpenMode;
  const sales = literal(input.auth, ["sales"]);
  const purchasing = literal(input.auth, ["manufacturing_purchasing", "manufacturing"]);
  const mfgDesk = literal(input.auth, ["manufacturing"]);
  const kb = literal(input.auth, ["trainer", "admin", "owner", "dev"]);
  const approvals = literal(input.auth, ["admin", "owner", "org_admin", "org_owner"]);
  const qc = literal(input.auth, ["mfg_qc", "mfg_final_qc", "owner", "org_owner"]);

  const landingReady: AttentionList["landingReady"] = {
    unsent_leads: sales && can("leads") && callbackReady(input, "unsent_leads"),
    open_qc: qc && can("mfgBuilderAssistant") && callbackReady(input, "open_qc"),
    negative_on_hand:
      purchasing && can("mfgInventory") && callbackReady(input, "negative_on_hand"),
    plant_material_request:
      purchasing && can("mfgPurchasing") && callbackReady(input, "plant_material_request"),
    ads_goal:
      sales &&
      can("adsAssistant") &&
      (input.adsTone === "amber" || input.adsTone === "red") &&
      callbackReady(input, "ads_goal"),
    stale_wac: mfgDesk && can("mfgInventory") && callbackReady(input, "stale_wac"),
    kb_gaps: kb && can("knowledgebase") && callbackReady(input, "kb_gaps"),
    ai_prompting_suggestions:
      kb && can("aiPrompting") && callbackReady(input, "ai_prompting_suggestions"),
    pending_approvals: approvals && can("admin") && callbackReady(input, "pending_approvals"),
  };

  const needsYou: string[] = [];
  const updates: string[] = [];
  const history: string[] = [];

  for (const id of input.sectionIds) {
    if (SKIP_SECTION_IDS.has(id)) continue;
    if (id === "sent-leads-30d") {
      if (sales) history.push(id);
      continue;
    }
    if (id === "ads-activity") {
      if (landingReady.ads_goal) needsYou.push(id);
      continue;
    }
    if (id === "unsent-leads" && input.unsentCount > 0 && landingReady.unsent_leads) {
      needsYou.push(id);
      continue;
    }
    if (id === "open-qc" && input.openQcCount > 0 && landingReady.open_qc) {
      needsYou.push(id);
      continue;
    }
    if (id === "plant-material-request") {
      if (input.plantRequestReviewCount > 0 && landingReady.plant_material_request) {
        needsYou.push(id);
      } else if (input.plantStatusCount > 0) {
        updates.push(id);
      }
      continue;
    }
    if (id === "stale-wac-reminder" && input.staleWacCount > 0 && landingReady.stale_wac) {
      needsYou.push(id);
      continue;
    }
    if (id === "kb-gaps" && input.kbGapOpenCount > 0 && landingReady.kb_gaps) {
      needsYou.push(id);
      continue;
    }
    if (
      id === "ai-prompting-suggestions" &&
      input.aiPromptingOpenCount > 0 &&
      landingReady.ai_prompting_suggestions
    ) {
      needsYou.push(id);
      continue;
    }
    if (
      id === "pending-member-approvals" &&
      input.pendingMemberApprovalTotal > 0 &&
      landingReady.pending_approvals
    ) {
      needsYou.push(id);
      continue;
    }
    if (id === "org-unsent-rollup" && input.orgUnsentRollupCount > 0 && can("dashboard")) {
      updates.push(id);
      continue;
    }
    if (UPDATE_SECTION_IDS.has(id)) updates.push(id);
  }

  return {
    groups: { needsYou, updates, history },
    showNegativeOnHand: input.negativeOnHandCount > 0 && landingReady.negative_on_hand === true,
    landingReady,
  };
}
